/**
 * Wind spot scoring engine — browser-side typed port of src/scorer.js.
 *
 * NOTE: src/scorer.js is still imported by api/forecast.js (the Vercel
 * serverless function runs as plain Node, no TS toolchain). Keep the two in
 * sync until P3 when api/forecast.js is converted to TS and src/scorer.js
 * can be deleted.
 *
 * ───────────────────────────────────────────────────────────────────────
 *  ROLE OF THE SCORER (post 2026-05-10 decoupling)
 * ───────────────────────────────────────────────────────────────────────
 *  `scoreSpot()` produces a combined direction-AND-speed `score` and
 *  `level` per hour. As of 2026-05-10 those values are NO LONGER used to
 *  colour the per-hour wind arrows (chart, hero card, day-strip, compass) —
 *  arrow colour now comes from `dirLevelColor()` in lib/utils.ts and is
 *  purely a function of wind direction match. See `docs/decisions.md`
 *  for the full rationale.
 *
 *  What `score` / `level` ARE still used for:
 *    • Spot ranking (overview pages, search) — combined judgement is
 *      appropriate when answering "is this day worth checking?"
 *    • Day-strip summary tinting at the day level (combined glanceable
 *      indicator across multiple-spot views)
 *    • The reason-string text on detail pages
 *
 *  What we deliberately don't do (yet):
 *    • Personalized ranges per user/gear. The current speed_min/speed_max
 *      assumes a generic mid-skill rider on a mid-range setup, which mis-
 *      represents what individual users can actually ride. That's why the
 *      arrow colour decoupling matters: speed bars now stand alone so each
 *      user can apply their own gear judgement. A future "smart score"
 *      feature could overlay personalised ratings on top of these signals.
 */

import { COMPASS_DIRS, type CompassDir, type MatchType, type Spot } from "./types";

const DIR_DEG: Record<CompassDir, number> = {
  N: 0, NNE: 22.5, NE: 45, ENE: 67.5,
  E: 90, ESE: 112.5, SE: 135, SSE: 157.5,
  S: 180, SSW: 202.5, SW: 225, WSW: 247.5,
  W: 270, WNW: 292.5, NW: 315, NNW: 337.5,
};

/** Shortest angular distance between two bearings (0–180). */
function angleDiff(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/** Convert Open-Meteo wind direction degrees to compass string. */
export function degreesToCompass(deg: number): CompassDir {
  const idx = Math.round(deg / 22.5) % 16;
  // noUncheckedIndexedAccess: idx is always in 0..15
  return COMPASS_DIRS[idx] as CompassDir;
}

export interface ScoreResult {
  score: number;
  matchType: MatchType;
  matchedDirs: CompassDir[];
  reason: string;
}

/** Score a spot against given wind conditions. */
export function scoreSpot(spot: Spot, dir: CompassDir, speedMs: number): ScoreResult {
  const deg = DIR_DEG[dir];

  const idealDegs = spot.ideal.dirs.map((d) => DIR_DEG[d]);
  const secDegs = (spot.secondary?.dirs ?? []).map((d) => DIR_DEG[d]);

  const minIdeal = idealDegs.length
    ? Math.min(...idealDegs.map((d) => angleDiff(d, deg)))
    : 999;
  const minSec = secDegs.length
    ? Math.min(...secDegs.map((d) => angleDiff(d, deg)))
    : 999;

  const speedOk = speedMs >= spot.ideal.speed_min && speedMs <= spot.ideal.speed_max;
  const speedLow = speedMs < spot.ideal.speed_min;
  const speedHigh = speedMs > spot.ideal.speed_max;

  let score = 0;
  let matchType: MatchType = "bad";
  let matchedDirs: CompassDir[] = [];

  if (minIdeal <= 22.5 && speedOk) {
    score = 100 - (minIdeal / 22.5) * 20;
    matchType = "good";
    matchedDirs = spot.ideal.dirs;
  } else if (minIdeal <= 22.5 && speedLow) {
    score = 55 - (minIdeal / 22.5) * 10;
    matchType = "ok";
    matchedDirs = spot.ideal.dirs;
  } else if (minIdeal <= 22.5 && speedHigh) {
    score = 60 - (minIdeal / 22.5) * 10;
    matchType = "ok";
    matchedDirs = spot.ideal.dirs;
  } else if (minIdeal <= 45 && speedOk) {
    score = 60 - (minIdeal / 45) * 25;
    matchType = "ok";
    matchedDirs = spot.ideal.dirs;
  } else if (minSec <= 22.5 && speedOk) {
    score = 50 - (minSec / 22.5) * 15;
    matchType = "ok";
    matchedDirs = spot.secondary.dirs;
  } else {
    score = Math.max(0, 20 - minIdeal * 0.15);
    matchType = "bad";
    matchedDirs = [];
  }

  return {
    score: Math.round(score),
    matchType,
    matchedDirs,
    reason: buildReason(spot, dir, speedMs, score, matchType),
  };
}

function buildReason(
  spot: Spot,
  dir: CompassDir,
  speedMs: number,
  score: number,
  matchType: MatchType,
): string {
  if (matchType === "bad") {
    return `${dir} wind doesn't suit this spot (works best in ${spot.ideal.dirs.join("/")})`;
  }

  const parts: string[] = [];
  if (score >= 80) {
    parts.push(`${dir} is bang in the ideal window`);
  } else if (score >= 60) {
    parts.push(`${dir} is within the good range`);
  } else {
    parts.push(`${dir} is marginal for this spot`);
  }

  if (speedMs < spot.ideal.speed_min) {
    parts.push(`wind at ${speedMs} m/s is a bit light (needs ${spot.ideal.speed_min}+)`);
  } else if (speedMs > spot.ideal.speed_max) {
    parts.push(`could be overpowered (usually tops out around ${spot.ideal.speed_max} m/s here)`);
  } else {
    parts.push(`${speedMs} m/s is solid`);
  }

  return `${parts.join(", ")}.`;
}

/** Rank all spots for given conditions. Sorted best first. */
export function rankSpots(spots: Spot[], dir: CompassDir, speedMs: number): Array<Spot & ScoreResult> {
  return spots
    .map((spot) => ({ ...spot, ...scoreSpot(spot, dir, speedMs) }))
    .sort((a, b) => b.score - a.score);
}
